import { type FormEvent, useState } from "react";
import { toast } from "sonner";
import { z } from "zod";
import { errorMessage, isApiError } from "@/api/client";
import { useUpdateOrganization } from "@/api/mutations";
import { type OrganizationDetail, PROVISIONAL_PLANS } from "@/api/types";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Field, FieldDescription, FieldError, FieldGroup, FieldLabel } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { NativeSelect, NativeSelectOption } from "@/components/ui/native-select";
import { Spinner } from "@/components/ui/spinner";
import { Switch } from "@/components/ui/switch";

const schema = z.object({
  name: z.string().trim().min(2, "Use at least 2 characters").max(120),
  plan: z.string().nullable(),
  offerApi: z.boolean(),
});

export function EditOrganizationDialog({ org, open, onOpenChange }: { org: OrganizationDetail; open: boolean; onOpenChange: (o: boolean) => void }) {
  const [name, setName] = useState(org.name);
  const [plan, setPlan] = useState(org.plan ?? "");
  const [offerApi, setOfferApi] = useState(org.settings.offerApi === true);
  const [error, setError] = useState<string | null>(null);
  const update = useUpdateOrganization(org.id);
  const submit = (e: FormEvent) => {
    e.preventDefault();
    const parsed = schema.safeParse({ name, plan: plan || null, offerApi });
    if (!parsed.success) return setError(parsed.error.issues[0]?.message ?? "Invalid");
    setError(null);
    update.mutate(parsed.data, {
      onSuccess: () => {
        toast.success("Organization updated");
        onOpenChange(false);
      },
      onError: (err) => {
        const fe = isApiError(err) ? (err.details as { fieldErrors?: Record<string, string> } | undefined)?.fieldErrors : undefined;
        setError(fe?.name ?? errorMessage(err));
      },
    });
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <form onSubmit={submit} noValidate>
          <DialogHeader>
            <DialogTitle>Edit organization</DialogTitle>
            <DialogDescription className="font-mono text-xs">{org.id}</DialogDescription>
          </DialogHeader>
          <FieldGroup className="my-4 gap-3">
            <Field data-invalid={Boolean(error)} className="gap-1">
              <FieldLabel htmlFor="org-name">Name</FieldLabel>
              <Input id="org-name" value={name} onChange={(e) => setName(e.target.value)} aria-invalid={Boolean(error)} />
              <FieldError className="text-xs">{error}</FieldError>
            </Field>
            <Field className="gap-1">
              <FieldLabel htmlFor="org-plan">Plan</FieldLabel>
              <NativeSelect id="org-plan" value={plan} onChange={(e) => setPlan(e.target.value)} className="w-full">
                <NativeSelectOption value="">No plan</NativeSelectOption>
                {PROVISIONAL_PLANS.map((p) => (
                  <NativeSelectOption key={p} value={p}>
                    {p}
                  </NativeSelectOption>
                ))}
              </NativeSelect>
            </Field>
            <Field orientation="horizontal" className="items-start gap-3 rounded-md border p-3">
              <Switch id="org-offer-api" checked={offerApi} onCheckedChange={setOfferApi} />
              <div className="space-y-0.5">
                <FieldLabel htmlFor="org-offer-api">Offer API</FieldLabel>
                <FieldDescription className="text-xs">
                  When Rogue has collected everything, it gets the max offer from this organization's offer API integrations (Integrations), for the stores
                  linked to them. Off: the dealer's team sets each max offer.
                </FieldDescription>
              </div>
            </Field>
          </FieldGroup>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={update.isPending}>
              {update.isPending ? <Spinner data-icon="inline-start" /> : null}
              Save
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
